// @ts-nocheck
/* d3 is loaded from CDN before this script -- typed as any since d3
   types with force simulation generics are extremely verbose and provide
   little value for a visualization-only module. The site.ts and
   bibliography.ts modules are fully strict-typed and tested. */
declare const d3: any;

interface GraphNode {
  id: number;
  name: string;
  papers: number;
  lastYear: number;
  firstYear: number;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface GraphLink {
  source: number | GraphNode;
  target: number | GraphNode;
  weight: number;
}

interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
  meta: { collaborators: number; connections: number };
}

(function () {
  const container = document.getElementById("pubgraph");
  if (!container) return;

  const dataUrl = container.getAttribute("data-src");
  if (!dataUrl) return;

  const width = container.clientWidth || 800;
  const height = 600;

  const svg = d3.select(container)
    .append("svg")
    .attr("viewBox", `0 0 ${width} ${height}`)
    .attr("width", "100%")
    .attr("height", height)
    .style("max-width", "100%")
    .style("border-radius", "8px")
    .style("background", "var(--pico-card-background-color, #f8f9fa)");

  const g = svg.append("g");

  svg.call(
    d3.zoom()
      .scaleExtent([0.3, 5])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      })
  );

  const tooltip = d3.select(container)
    .append("div")
    .style("position", "absolute")
    .style("pointer-events", "none")
    .style("background", "var(--pico-background-color, #fff)")
    .style("border", "1px solid var(--pico-muted-border-color, #ccc)")
    .style("border-radius", "6px")
    .style("padding", "8px 12px")
    .style("font-size", "0.85rem")
    .style("box-shadow", "0 2px 8px rgba(0,0,0,0.15)")
    .style("opacity", 0)
    .style("z-index", "10");

  d3.json(dataUrl).then((data) => {
    if (!data) return;
    const { nodes, links } = data;

    const yearExtent = d3.extent(nodes, (d) => d.lastYear);
    const paperExtent = d3.extent(nodes, (d) => d.papers);

    const colorScale = d3.scaleLinear()
      .domain(yearExtent)
      .range(["#1565C0", "#FF655D"]);

    const radiusScale = d3.scaleSqrt()
      .domain(paperExtent)
      .range([4, 20]);

    const edgeExtent = d3.extent(links, (d) => d.weight);
    const edgeScale = d3.scaleLinear()
      .domain(edgeExtent)
      .range([0.5, 3]);

    const simulation = d3.forceSimulation(nodes)
      .force("link", d3.forceLink(links).id((d) => d.id).distance(60))
      .force("charge", d3.forceManyBody().strength(-80))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius((d) => radiusScale(d.papers) + 2));

    const link = g.append("g")
      .selectAll("line")
      .data(links)
      .join("line")
      .attr("stroke", "#999")
      .attr("stroke-opacity", 0.3)
      .attr("stroke-width", (d) => edgeScale(d.weight));

    const node = g.append("g")
      .selectAll("circle")
      .data(nodes)
      .join("circle")
      .attr("r", (d) => radiusScale(d.papers))
      .attr("fill", (d) => colorScale(d.lastYear))
      .attr("stroke", "#333")
      .attr("stroke-width", 0.5)
      .attr("opacity", 0.9)
      .style("cursor", "pointer")
      .call(makeDrag(simulation));

    const labelThreshold = Math.max(
      3,
      d3.quantile(nodes.map((d) => d.papers).sort(d3.ascending), 0.7) ?? 3
    );

    const label = g.append("g")
      .selectAll("text")
      .data(nodes.filter((d) => d.papers >= labelThreshold))
      .join("text")
      .text((d) => d.name.split(" ").pop() ?? "")
      .attr("font-size", "9px")
      .attr("font-family", "Jost, sans-serif")
      .attr("fill", "var(--pico-color, #333)")
      .attr("text-anchor", "middle")
      .attr("dy", (d) => -radiusScale(d.papers) - 4)
      .style("pointer-events", "none");

    node.on("click", (_event, d) => {
      const lastName = d.name.split(" ").pop() ?? "";
      window.location.href = `/publications/?q=${encodeURIComponent(lastName)}`;
    });

    node.on("mouseover", function (_event, d) {
      tooltip.style("opacity", 1)
        .html(
          `<strong>${d.name}</strong><br>` +
          `${d.papers} joint publication${d.papers > 1 ? "s" : ""}<br>` +
          `<span style='color:var(--pico-muted-color)'>${d.firstYear} &ndash; ${d.lastYear}</span><br>` +
          `<small style='color:var(--pico-primary)'>Click to search publications</small>`
        );
      d3.select(this).attr("stroke-width", 2).attr("stroke", "#FF655D");

      link.attr("stroke-opacity", (l) =>
        (l.source as any).id === d.id || (l.target as any).id === d.id ? 0.8 : 0.08
      ).attr("stroke", (l) =>
        (l.source as any).id === d.id || (l.target as any).id === d.id ? "#FF655D" : "#999"
      );

      node.attr("opacity", (n) => {
        if (n.id === d.id) return 1;
        const connected = links.some((l) =>
          ((l.source as any).id === d.id && (l.target as any).id === n.id) ||
          ((l.target as any).id === d.id && (l.source as any).id === n.id)
        );
        return connected ? 0.9 : 0.15;
      });
    }).on("mousemove", (event) => {
      const rect = container.getBoundingClientRect();
      tooltip
        .style("left", `${event.clientX - rect.left + 12}px`)
        .style("top", `${event.clientY - rect.top - 10}px`);
    }).on("mouseout", function () {
      tooltip.style("opacity", 0);
      d3.select(this).attr("stroke-width", 0.5).attr("stroke", "#333");
      link.attr("stroke-opacity", 0.3).attr("stroke", "#999");
      node.attr("opacity", 0.9);
    });

    simulation.on("tick", () => {
      link
        .attr("x1", (d) => (d.source as GraphNode).x!)
        .attr("y1", (d) => (d.source as GraphNode).y!)
        .attr("x2", (d) => (d.target as GraphNode).x!)
        .attr("y2", (d) => (d.target as GraphNode).y!);
      node.attr("cx", (d) => d.x!).attr("cy", (d) => d.y!);
      label.attr("x", (d) => d.x!).attr("y", (d) => d.y!);
    });

    /* Legend */
    const legendW = 140, legendH = 12;
    const legendG = svg.append("g")
      .attr("transform", `translate(${width - legendW - 20}, 20)`);

    const gradient = svg.append("defs")
      .append("linearGradient").attr("id", "recency-grad");
    gradient.append("stop").attr("offset", "0%").attr("stop-color", "#1565C0");
    gradient.append("stop").attr("offset", "100%").attr("stop-color", "#FF655D");

    legendG.append("rect").attr("width", legendW).attr("height", legendH)
      .attr("rx", 3).style("fill", "url(#recency-grad)");

    const legendText = (x: number, y: number, text: string | number, anchor = "start") =>
      legendG.append("text").attr("x", x).attr("y", y).text(String(text))
        .attr("font-size", "10px").attr("fill", "var(--pico-color, #333)")
        .attr("text-anchor", anchor).attr("font-family", "Jost, sans-serif");

    legendText(0, legendH + 14, yearExtent[0]);
    legendText(legendW, legendH + 14, yearExtent[1], "end");
    legendG.append("text").attr("x", legendW / 2).attr("y", -4)
      .text("Most recent collaboration")
      .attr("font-size", "10px").attr("fill", "var(--pico-muted-color, #666)")
      .attr("text-anchor", "middle").attr("font-family", "Jost, sans-serif");

    svg.append("text").attr("x", 15).attr("y", height - 10)
      .text(`${data.meta.collaborators} collaborators, ${data.meta.connections} connections`)
      .attr("font-size", "11px").attr("fill", "var(--pico-muted-color, #666)")
      .attr("font-family", "Jost, sans-serif");

  }).catch((err) => {
    container.innerHTML = "<p>Failed to load publication network data.</p>";
    console.error("pubgraph:", err);
  });

  function makeDrag(simulation: any) {
    return d3.drag()
      .on("start", (event, d) => {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on("drag", (event, d) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on("end", (event, d) => {
        if (!event.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      });
  }
})();
